import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { 
  Calendar, Search, Filter, Car, Clock, 
  Loader2, ChevronLeft, ChevronRight, ArrowDown, ArrowUp,
  Plane, MapPin, Printer, Download
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar as CalendarComponent } from './ui/calendar';
import { adminApi } from '../lib/api';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  CONFIRMED: 'bg-success/20 text-success',
  COMPLETED: 'bg-blue-500/20 text-blue-400',
  PENDING_PAYMENT: 'bg-warning/20 text-warning',
  CANCELLED: 'bg-red-500/20 text-red-400',
  NO_SHOW: 'bg-zinc-500/20 text-zinc-400'
};

const CHANNEL_COLORS = {
  DIRECT: 'bg-primary/20 text-primary',
  PARKOS: 'bg-blue-500/20 text-blue-400',
  PARKVIA: 'bg-green-500/20 text-green-400',
  ONEPARK: 'bg-purple-500/20 text-purple-400',
  LOOKING4PARKING: 'bg-orange-500/20 text-orange-400',
  FREE2MOVE: 'bg-pink-500/20 text-pink-400'
};

const ReservationsFilter = ({ onDataUpdate }) => {
  const { i18n } = useTranslation();
  const locale = i18n.language === 'es' ? es : enUS;
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filterType, setFilterType] = useState('all'); // 'all', 'arrivals', 'departures'
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await adminApi.getReservationsByDate(dateStr, filterType);
      setData(response.data);
      onDataUpdate?.(response.data);
    } catch (error) {
      console.error('Error fetching reservations:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, filterType, onDataUpdate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const goToPreviousDay = () => {
    setSelectedDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 1);
      return newDate;
    });
  };

  const goToNextDay = () => {
    setSelectedDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 1);
      return newDate;
    });
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const getReservationList = () => {
    if (!data) return [];
    if (filterType === 'arrivals') return data.arrivals || [];
    if (filterType === 'departures') return data.departures || [];
    return data.reservations || [];
  };

  const reservations = getReservationList();

  const handlePrint = () => {
    // Escapa HTML para prevenir XSS al inyectar datos en document.write
    const esc = (val) => String(val ?? '').replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
    const dateStr = format(selectedDate, "EEEE dd 'de' MMMM yyyy", { locale });
    const filterLabel = filterType === 'arrivals' ? 'Llegadas' : filterType === 'departures' ? 'Salidas' : 'Todas las reservas';
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Reservas ${dateStr} - Pleyades Parking</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1a1a1a; font-size: 24px; margin-bottom: 5px; }
          h2 { color: #666; font-size: 14px; font-weight: normal; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th { background: #1a1a1a; color: #FFD700; padding: 10px 8px; text-align: left; }
          td { padding: 8px; border-bottom: 1px solid #ddd; }
          tr:nth-child(even) { background: #f9f9f9; }
          .arrival { color: #22c55e; }
          .departure { color: #3b82f6; }
          .summary { margin-top: 20px; padding: 15px; background: #f5f5f5; border-radius: 8px; display: flex; gap: 30px; }
          .summary-item { }
          .summary-label { color: #666; font-size: 12px; }
          .summary-value { font-size: 20px; font-weight: bold; }
          @media print {
            body { padding: 10px; }
          }
        </style>
      </head>
      <body>
        <h1>🚗 PLEYADES PARKING</h1>
        <h2>${filterLabel} - ${dateStr}</h2>
        
        <div class="summary">
          <div class="summary-item">
            <div class="summary-label">Total</div>
            <div class="summary-value">${reservations.length}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Llegadas</div>
            <div class="summary-value" style="color: #22c55e">${data?.arrivals_count || 0}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Salidas</div>
            <div class="summary-value" style="color: #3b82f6">${data?.departures_count || 0}</div>
          </div>
        </div>
        
        <table style="margin-top: 20px;">
          <thead>
            <tr>
              <th>Hora</th>
              <th>Tipo</th>
              <th>Matrícula</th>
              <th>Cliente</th>
              <th>Teléfono</th>
              <th>Terminal</th>
              <th>Estado</th>
              <th>Observaciones</th>
            </tr>
          </thead>
          <tbody>
            ${reservations.map(r => {
              const isArrival = filterType === 'arrivals' || (filterType === 'all' && format(new Date(r.start_dt), 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd'));
              const timeStr = isArrival 
                ? format(new Date(r.start_dt), 'HH:mm')
                : format(new Date(r.end_dt), 'HH:mm');
              return `
              <tr>
                <td><strong>${esc(timeStr)}</strong></td>
                <td class="${isArrival ? 'arrival' : 'departure'}">${isArrival ? '⬇️ Llegada' : '⬆️ Salida'}</td>
                <td><strong>${esc(r.vehicle_plate || '-')}</strong></td>
                <td>${esc(r.customer_name || '-')}</td>
                <td>${esc(r.customer_phone || '-')}</td>
                <td>${esc(isArrival ? (r.arrival_terminal || 'T4') : (r.departure_terminal || 'T4'))}</td>
                <td>${r.payment_status === 'PAID' ? '✅' : '⏳'} ${esc(r.status)}</td>
                <td style="font-size: 11px; color: #444;">${esc(r.notes || '-')}</td>
              </tr>
            `}).join('')}
          </tbody>
        </table>
        
        <p style="margin-top: 30px; font-size: 10px; color: #999; text-align: center;">
          Impreso el ${format(new Date(), "dd/MM/yyyy 'a las' HH:mm")} | Pleyades Parking
        </p>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  const handleDownloadTicket = async (reservationCode) => {
    try {
      const response = await fetch(`${API_URL}/api/invoices/receipt/${reservationCode}`, {
        method: 'GET',
      });
      
      if (!response.ok) {
        throw new Error('Error al descargar el ticket');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Ticket_${reservationCode}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Ticket ${reservationCode} descargado`);
    } catch (error) {
      console.error('Error downloading ticket:', error);
      toast.error('Error al descargar el ticket');
    }
  };

  return (
    <div className="space-y-4" data-testid="reservations-filter">
      {/* Date Selector & Filters */}
      <div className="flex flex-wrap items-center gap-4 bg-white/5 rounded-xl p-4 border border-white/10">
        {/* Date Navigation */}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={goToPreviousDay}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="bg-white/10 border-white/20 min-w-[180px]"
                data-testid="date-picker"
              >
                <Calendar className="w-4 h-4 mr-2" />
                {format(selectedDate, 'EEEE d MMMM yyyy', { locale })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-[#1a1a1a] border-white/20 z-[100] shadow-2xl" align="start">
              <CalendarComponent
                mode="single"
                selected={selectedDate}
                onSelect={(date) => {
                  if (date) setSelectedDate(date);
                  setDateOpen(false);
                }}
                locale={locale}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          
          <Button variant="ghost" size="sm" onClick={goToNextDay}>
            <ChevronRight className="w-5 h-5" />
          </Button>
          
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={goToToday}
            className="ml-2"
          >
            {i18n.language === 'es' ? 'Hoy' : 'Today'}
          </Button>
        </div>

        {/* Filter Type */}
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-sm text-zinc-400">
            {i18n.language === 'es' ? 'Mostrar:' : 'Show:'}
          </span>
          <div className="flex gap-1 bg-black/30 rounded-lg p-1">
            <button
              onClick={() => setFilterType('all')}
              className={cn(
                "px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                filterType === 'all' 
                  ? 'bg-primary text-black' 
                  : 'text-zinc-400 hover:text-white'
              )}
            >
              {i18n.language === 'es' ? 'Todo' : 'All'}
            </button>
            <button
              onClick={() => setFilterType('arrivals')}
              className={cn(
                "px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1",
                filterType === 'arrivals' 
                  ? 'bg-green-500 text-white' 
                  : 'text-zinc-400 hover:text-white'
              )}
            >
              <ArrowDown className="w-3 h-3" />
              {i18n.language === 'es' ? 'Llegadas' : 'Arrivals'}
            </button>
            <button
              onClick={() => setFilterType('departures')}
              className={cn(
                "px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1",
                filterType === 'departures' 
                  ? 'bg-blue-500 text-white' 
                  : 'text-zinc-400 hover:text-white'
              )}
            >
              <ArrowUp className="w-3 h-3" />
              {i18n.language === 'es' ? 'Salidas' : 'Departures'}
            </button>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      {data && (
        <div className="flex items-center gap-4">
          <div className="grid grid-cols-3 gap-4 flex-1">
            <div className={cn(
              "rounded-lg p-3 text-center",
              filterType === 'all' ? 'bg-primary/20 border border-primary/30' : 'bg-white/5'
            )}>
              <p className="text-2xl font-bold text-white">{data.total}</p>
              <p className="text-xs text-zinc-400">{i18n.language === 'es' ? 'Total' : 'Total'}</p>
            </div>
            <div className={cn(
              "rounded-lg p-3 text-center",
              filterType === 'arrivals' ? 'bg-green-500/20 border border-green-500/30' : 'bg-white/5'
            )}>
              <p className="text-2xl font-bold text-green-500">{data.arrivals_count}</p>
              <p className="text-xs text-zinc-400">{i18n.language === 'es' ? 'Llegadas' : 'Arrivals'}</p>
            </div>
            <div className={cn(
              "rounded-lg p-3 text-center",
              filterType === 'departures' ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-white/5'
            )}>
              <p className="text-2xl font-bold text-blue-500">{data.departures_count}</p>
              <p className="text-xs text-zinc-400">{i18n.language === 'es' ? 'Salidas' : 'Departures'}</p>
            </div>
          </div>
          
          {/* Print Button */}
          <Button
            onClick={handlePrint}
            className="bg-white/10 hover:bg-white/20 text-white border border-white/20 h-[70px] px-6"
            data-testid="print-reservations-btn"
          >
            <Printer className="w-5 h-5 mr-2" />
            Imprimir
          </Button>
        </div>
      )}

      {/* Reservations List */}
      <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : reservations.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            {i18n.language === 'es' 
              ? 'No hay reservas para esta fecha' 
              : 'No reservations for this date'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-black/30 border-b border-white/10">
                  <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">
                    {i18n.language === 'es' ? 'Código' : 'Code'}
                  </th>
                  <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">
                    {i18n.language === 'es' ? 'Cliente' : 'Customer'}
                  </th>
                  <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">
                    {i18n.language === 'es' ? 'Vehículo' : 'Vehicle'}
                  </th>
                  <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">
                    {i18n.language === 'es' ? 'Entrada' : 'Entry'}
                  </th>
                  <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">
                    {i18n.language === 'es' ? 'Salida' : 'Exit'}
                  </th>
                  <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">
                    {i18n.language === 'es' ? 'Terminal' : 'Terminal'}
                  </th>
                  <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">
                    {i18n.language === 'es' ? 'Canal' : 'Channel'}
                  </th>
                  <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">
                    {i18n.language === 'es' ? 'Estado' : 'Status'}
                  </th>
                  <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">
                    {i18n.language === 'es' ? 'Pago' : 'Payment'}
                  </th>
                  <th className="text-right text-zinc-400 text-xs font-medium py-3 px-4">
                    Total
                  </th>
                  <th className="text-center text-zinc-400 text-xs font-medium py-3 px-4">
                    {i18n.language === 'es' ? 'Acciones' : 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {reservations.map((r, index) => {
                  const isArrivalToday = r.start_dt?.substring(0, 10) === format(selectedDate, 'yyyy-MM-dd');
                  const isDepartureToday = r.end_dt?.substring(0, 10) === format(selectedDate, 'yyyy-MM-dd');
                  const isPaid = r.payment_status === 'PAID';
                  
                  return (
                    <tr 
                      key={r.id || index}
                      className={cn(
                        "border-b border-white/5 hover:bg-white/5 transition-colors",
                        isArrivalToday && filterType !== 'departures' && 'border-l-2 border-l-green-500',
                        isDepartureToday && filterType !== 'arrivals' && 'border-l-2 border-l-blue-500'
                      )}
                    >
                      <td className="py-3 px-4">
                        <span className="font-mono text-primary font-medium">
                          {r.reservation_code}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-white text-sm">{r.customer_name}</p>
                        <p className="text-xs text-zinc-500">{r.customer_phone}</p>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-mono text-white">{r.vehicle_plate}</span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          {isArrivalToday && <ArrowDown className="w-3 h-3 text-green-500" />}
                          <span className={cn(
                            "text-sm",
                            isArrivalToday ? "text-green-500 font-medium" : "text-zinc-400"
                          )}>
                            {r.start_dt?.substring(0, 16).replace('T', ' ')}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          {isDepartureToday && <ArrowUp className="w-3 h-3 text-blue-500" />}
                          <span className={cn(
                            "text-sm",
                            isDepartureToday ? "text-blue-500 font-medium" : "text-zinc-400"
                          )}>
                            {r.end_dt?.substring(0, 16).replace('T', ' ')}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col text-xs">
                          {r.arrival_terminal && (
                            <span className="text-green-500">{r.arrival_terminal}</span>
                          )}
                          {r.departure_terminal && (
                            <span className="text-blue-500">{r.departure_terminal}</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={cn(
                          "text-xs px-2 py-1 rounded",
                          CHANNEL_COLORS[r.source_channel] || CHANNEL_COLORS.DIRECT
                        )}>
                          {r.source_channel || 'DIRECT'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={cn(
                          "text-xs px-2 py-1 rounded",
                          STATUS_COLORS[r.status] || 'bg-zinc-500/20 text-zinc-400'
                        )}>
                          {r.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={cn(
                          "text-xs px-2 py-1 rounded font-semibold",
                          isPaid 
                            ? (r.paid_via_channel ? 'bg-blue-500/20 text-blue-400' : 'bg-success/20 text-success')
                            : 'bg-warning/20 text-warning'
                        )}>
                          {isPaid 
                            ? (r.paid_via_channel ? 'PREPAGADO' : 'PAGADO') 
                            : 'COBRAR'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-white font-medium">
                          {(r.total_cents / 100).toFixed(2)}€
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadTicket(r.reservation_code)}
                          className="text-primary hover:text-primary/80 hover:bg-primary/10"
                          title={i18n.language === 'es' ? 'Descargar ticket' : 'Download ticket'}
                          data-testid={`download-ticket-${r.reservation_code}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReservationsFilter;
